import { Effect } from "@babylonjs/core/Materials/effect";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Geometry } from "@babylonjs/core/Meshes/geometry";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Log } from "../../utils";
import { ERR_INVALID_ARGUMENT } from "../../utils/error";
import { Camera } from "../view/Camera";
export class ThickLinesMesh extends Mesh {
    constructor(pName, pScene, pParent = null, pSource, pDoNotCloneChildren) {
        super(pName, pScene, pParent, pSource, pDoNotCloneChildren);
        this.onBeforeCameraRender = (pCamera) => {
            const tEngine = this.getEngine();
            let tWidth = pCamera.viewport.width * tEngine.getRenderWidth();
            let tHeight = pCamera.viewport.height * tEngine.getRenderHeight();
            if (pCamera instanceof Camera) {
                tWidth /= pCamera.view.pixelScaleFactor;
                tHeight /= pCamera.view.pixelScaleFactor;
            }
            if (this._lastViewportSize.x !== tWidth || this._lastViewportSize.y !== tHeight) {
                this._lastViewportSize.x = tWidth;
                this._lastViewportSize.y = tHeight;
                this._lineMaterial.setVector2("viewport", this._lastViewportSize);
            }
        };
        if (pSource) {
            this.color = pSource.color.clone();
            this.thickness = pSource.thickness;
        }
        else {
            this.color = new Color4(1, 1, 1, 1);
            this.thickness = 1;
        }
        this._lineMaterial = new ShaderMaterial("ThickLines", pScene, "wcf.core.ThickLines", {
            attributes: ["position", "position2", "direction"],
            uniforms: ["worldViewProjection", "thickness", "viewport", "color"]
        });
        this._lineMaterial.backFaceCulling = false;
        this._lineMaterial.needAlphaBlending = () => {
            return (this.color.a < 1.0);
        };
        this._lineMaterial.freeze();
        this._lastViewportSize = new Vector2(-1, -1);
        pScene.onBeforeCameraRenderObservable.add(this.onBeforeCameraRender);
    }
    get material() {
        return (this._lineMaterial);
    }
    set material(pValue) {
        // Do not allow to change the material
    }
    get checkCollisions() {
        return (false);
    }
    set checkCollisions(pValue) {
        // Do not allow to change this
    }
    getClassName() {
        return ("ThickLinesMesh");
    }
    createInstance(name) {
        Log.warn("ThickLinesMeshes do not support createInstance.");
        return (null);
    }
    _bind(pSubMesh, pEffect, pFillMode) {
        this._lineMaterial.setColor4("color", this.color);
        this._lineMaterial.setFloat("thickness", this.thickness);
        super._bind(pSubMesh, pEffect, pFillMode);
        return (this);
    }
    dispose(pDoNotRecurse, pDisposeMaterialAndTextures) {
        this._lineMaterial.dispose();
        this.getScene().onBeforeCameraRenderObservable.removeCallback(this.onBeforeCameraRender);
        super.dispose(pDoNotRecurse, pDisposeMaterialAndTextures);
    }
    clone(pName, pNewParent, pDoNotCloneChildren) {
        return (new ThickLinesMesh(pName, this.getScene(), pNewParent, this, pDoNotCloneChildren));
    }
    static CreateThickLines(pName, pScene, pLines) {
        if (pLines == null || pLines.length < 2)
            throw new Error(ERR_INVALID_ARGUMENT);
        //each vertex has the following attributes:
        // vec3 position   //current point on line
        // vec3 position2  //next point on line
        // float direction //a sign, -1 or 1
        //get the positional data for each vertex
        const tSegmentCount = pLines.length / 2;
        const tPositions = duplicatePoints(pLines);
        const tPositions2 = duplicatePoints(createPositions2(pLines));
        const tDirections = createDirections(pLines.length);
        const tIndices = createIndices(tSegmentCount);
        const tGeom = new Geometry(Geometry.RandomId(), pScene);
        tGeom.setVerticesData(VertexBuffer.PositionKind, createFloatArray(tPositions), false);
        tGeom.setVerticesData("position2", createFloatArray(tPositions2), false, 3);
        tGeom.setVerticesData("direction", tDirections, false, 1);
        tGeom.setIndices(tIndices);
        const tMesh = new ThickLinesMesh(pName, pScene);
        tGeom.applyToMesh(tMesh);
        return (tMesh);
    }
}
//// helper functions //////////////////////////////////////////////////////////////////////////
//duplcates all entries in the given array
function duplicatePoints(pArray) {
    const tOut = new Array();
    pArray.forEach(v => tOut.push(v, v));
    return (tOut);
}
//each pair has a mirrored direction
function createDirections(pLength) {
    let tSign = 1;
    const tDirections = new Float32Array(pLength * 2);
    for (let i = 0; i < pLength; i++) {
        tDirections[i * 2 + 0] = -tSign;
        tDirections[i * 2 + 1] = tSign;
        tSign *= -1;
    }
    return (tDirections);
}
function createPositions2(pPositions) {
    const tPositions2 = new Array();
    for (let i = 0; i < pPositions.length; i += 2) {
        tPositions2.push(pPositions[i + 1]);
        tPositions2.push(pPositions[i]);
    }
    return (tPositions2);
}
function createIndices(pSegmentCount) {
    const tIndices = new Uint16Array(pSegmentCount * 6);
    for (let i = 0; i < pSegmentCount; i++) {
        const tIndex = i * 6;
        const j = i * 4; //segment offset
        tIndices[tIndex + 0] = j + 0;
        tIndices[tIndex + 1] = j + 1;
        tIndices[tIndex + 2] = j + 2;
        tIndices[tIndex + 3] = j + 2;
        tIndices[tIndex + 4] = j + 1;
        tIndices[tIndex + 5] = j + 3;
    }
    return (tIndices);
}
function createFloatArray(pSource) {
    const tArray = new Float32Array(pSource.length * 3);
    let i = 0;
    for (const tV of pSource) {
        tArray[i++] = tV.x;
        tArray[i++] = tV.y;
        tArray[i++] = tV.z;
    }
    return (tArray);
}
//register shaders
Effect.ShadersStore["wcf.core.ThickLinesVertexShader"] = `
precision highp float;

attribute vec3 position;
attribute vec3 position2;
attribute float direction;

uniform mat4 worldViewProjection;
uniform vec2 viewport;
uniform float thickness;

vec2 projectToScreen(vec4 projected) {
    vec3 projected_normal = projected.xyz / projected.w;
    vec2 pos = (projected_normal * 0.5 + 0.5).xy;
    return pos * viewport;
}

vec4 unprojectFromScreen(vec2 screen, float z, float w) {
    vec2 clip_pos = screen / viewport;
    vec2 device_normal = clip_pos * 2.0 - 1.0;
    return vec4(device_normal * w, z, w);
}

void main(void)
{
    vec4 currentProjected = worldViewProjection * vec4(position, 1.0);
    vec4 nextProjected = worldViewProjection * vec4(position2, 1.0);

    vec2 currentScreen = projectToScreen(currentProjected);
    vec2 nextScreen = projectToScreen(nextProjected);

    float offsetLength = thickness * 0.5;

    vec2 dir = normalize(nextScreen - currentScreen);
    vec2 offsetDir = vec2(-dir.y, dir.x);
    offsetDir *= offsetLength * direction;
    vec2 offsetPos = currentScreen + offsetDir;

    gl_Position = unprojectFromScreen(offsetPos, currentProjected.z, currentProjected.w);
}`;
Effect.ShadersStore["wcf.core.ThickLinesFragmentShader"] = `
precision highp float;

uniform vec4 color;

void main(void)
{
    gl_FragColor = color;
}`;
